import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';

import {
  backdropActiveClass,
  backdropBaseClass,
  backdropInactiveClass,
  closeButtonClass,
  contentClass,
  profileMenuActiveClass,
  profileMenuBaseClass,
  profileMenuDesktopClass,
  profileMenuInactiveClass,
  profileMenuMobileClass,
} from './SideNav.css';
import { CloseIcon } from '../../icons';
import { useI18nTranslation } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { Text } from '../Text/Text';

type SideNavProps = {
  isDesktop: boolean;
  /** Controlled state of the SideNav */
  isOpen: boolean;
  isCloseButtonVisible?: boolean;
  /** External state setter that is triggered when the menu is closed */
  setIsOpen: (open: boolean) => void;
  children?: ReactNode;
};

export const SideNav: FC<SideNavProps> = ({ isOpen, setIsOpen, children, isDesktop, isCloseButtonVisible = true }) => {
  const onAnimationEnd = useCallback(() => {
    if (!isOpen) setIsVisible(false);
  }, [isOpen]);

  const closeText = useI18nTranslation('close');
  const capitalizedCloseText = closeText.charAt(0).toUpperCase() + closeText.slice(1);

  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen && !isVisible) {
      setIsVisible(true);
    }

    if (isOpen) {
      window.scrollTo(0, 0);
    }
  }, [isOpen, isVisible]);

  if (!isVisible) return null;

  const CloseButton: FC = () => (
    <button type="button" className={closeButtonClass} onClick={() => setIsOpen(false)}>
      <CloseIcon />
      <Box paddingLeft="2">
        <Text weight="bold">{capitalizedCloseText}</Text>
      </Box>
    </button>
  );

  const profileMenuClassName = getClassNames(
    profileMenuBaseClass,
    isDesktop ? profileMenuDesktopClass : profileMenuMobileClass,
    {
      [profileMenuActiveClass]: isOpen,
      [profileMenuInactiveClass]: !isOpen,
    },
  );

  const backdropClassName = getClassNames(backdropBaseClass, {
    [backdropActiveClass]: isOpen,
    [backdropInactiveClass]: !isOpen,
  });

  return isDesktop ? (
    <Dialog.Root onOpenChange={setIsOpen} open>
      <Dialog.Portal>
        <Dialog.Overlay className={backdropClassName}>
          <Dialog.Content className={contentClass}>
            <div className={profileMenuClassName} data-cy="dropMenu" onAnimationEnd={onAnimationEnd}>
              {isCloseButtonVisible && <CloseButton />}
              {children}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  ) : (
    <div className={profileMenuClassName} data-cy="dropMenu" onAnimationEnd={onAnimationEnd}>
      {isCloseButtonVisible && <CloseButton />}
      {children}
    </div>
  );
};
